import React from 'react';
import { Bot, MessageSquare, Lightbulb, Mic, X, ChevronDown } from 'lucide-react';
import { useAIHealthCompanionStore } from '@/stores/ai-health-companion-store';
import { cn } from '@/lib/utils';
import type { CompanionPanelMode, AIConversationMode } from '@/types/ai-health-companion-types';

interface CompanionHeaderProps {
  onClose: () => void;
}

const CONVERSATION_MODES: Array<{
  value: AIConversationMode;
  label: string;
}> = [
  { value: 'general', label: 'General Chat' },
  { value: 'symptom-assessment', label: 'Symptom Assessment' },
  { value: 'test-explanation', label: 'Test Explanation' },
  { value: 'medication-review', label: 'Medication Review' },
  { value: 'preventive-care', label: 'Preventive Care' },
];

const CompanionHeader: React.FC<CompanionHeaderProps> = ({ onClose }) => {
  const { panelMode, setPanelMode, conversationMode, setConversationMode } =
    useAIHealthCompanionStore();

  const [showConversationModeDropdown, setShowConversationModeDropdown] =
    React.useState(false);

  const modes: Array<{ value: CompanionPanelMode; icon: React.ReactNode; label: string }> = [
    { value: 'chat', icon: <MessageSquare className="w-4 h-4" />, label: 'Chat' },
    { value: 'insights', icon: <Lightbulb className="w-4 h-4" />, label: 'Insights' },
    { value: 'voice', icon: <Mic className="w-4 h-4" />, label: 'Voice' },
  ];

  const handleModeChange = (mode: CompanionPanelMode) => {
    setPanelMode(mode);
  };

  const handleConversationModeChange = (mode: AIConversationMode) => {
    setConversationMode(mode);
    setShowConversationModeDropdown(false);
  };

  const currentConversationModeLabel =
    CONVERSATION_MODES.find((m) => m.value === conversationMode)?.label || 'General Chat';

  return (
    <div className="flex flex-col border-b border-gray-200 dark:border-gray-800">
      {/* Main Header Row */}
      <div className="px-4 py-3 flex items-center justify-between">
        {/* Title */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Health Companion
          </h2>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Close companion panel"
        >
          <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      {/* Mode Tabs */}
      <div className="px-4 pb-3 flex items-center gap-2">
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          {modes.map((mode) => (
            <button
              key={mode.value}
              onClick={() => handleModeChange(mode.value)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                panelMode === mode.value
                  ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              )}
            >
              {mode.icon}
              <span>{mode.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Conversation Mode Dropdown (Only in Chat Mode) */}
      {panelMode === 'chat' && (
        <div className="px-4 pb-3 relative">
          <button
            onClick={() => setShowConversationModeDropdown(!showConversationModeDropdown)}
            className={cn(
              'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm',
              'bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700',
              'hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
            )}
          >
            <span className="text-gray-700 dark:text-gray-300 font-medium">
              {currentConversationModeLabel}
            </span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform',
                showConversationModeDropdown && 'rotate-180'
              )}
            />
          </button>

          {/* Dropdown Menu */}
          {showConversationModeDropdown && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowConversationModeDropdown(false)}
              />
              {/* Menu */}
              <div className="absolute top-full left-4 right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 overflow-hidden">
                {CONVERSATION_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => handleConversationModeChange(mode.value)}
                    className={cn(
                      'w-full px-3 py-2.5 text-left text-sm transition-colors',
                      conversationMode === mode.value
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    )}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(CompanionHeader);
